import { Box, Image, InlineStack, Text, Tooltip } from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { Icon } from '@customerio/pluma-components/react';
import type { Source } from '@customerio/hydra-ui-api';
import { ACTIVITY_LOG_SOURCE } from '@/utils/activity-log-source-types';
import { getSourceIconInfo, groupSourcesByType } from '../utils/sources';
import * as styles from './sources-cell.css';

/** How many source marks a cell shows before collapsing the rest into a count. */
const MAX_DISPLAY_SOURCES = 3;

/**
 * Where the marks are being drawn. A list cell has one column to fit, so it
 * keeps three and collapses the rest into a count; the details panel has the
 * width to show every one and wraps them, and writes its "none" as an em
 * dash rather than a hyphen — both as the page this replaces does
 * (`data-index-list-table/sources-cell/index.gts:68-83` against
 * `item-details/usage/index.gts:435-447`).
 */
export type SourcesVariant = 'cell' | 'panel';

export type SourcesCellProps = {
  sources: readonly Source[];
  variant?: SourcesVariant;
};

/**
 * Where a row's data came from.
 *
 * Repeat writes from the same place collapse into one mark, so a row written
 * a thousand times by one campaign does not read as a thousand sources.
 */
export function SourcesCell({ sources, variant = 'cell' }: SourcesCellProps) {
  // One mark per *source* in the panel, one per *type* in a cell. The page
  // this replaces groups only where the column is narrow
  // (`data-index-list-table/sources-cell/index.gts:57-60` against
  // `item-details/usage/index.gts:441-444`, which iterates the sources
  // themselves), and on an attribute written from eighty places the
  // difference is eighty marks against six.
  const groups = variant === 'panel' ? sources.map((source) => [source]) : groupSourcesByType([...sources]);

  if (groups.length === 0) {
    return variant === 'panel' ? <Text>—</Text> : <Text color="placeholder">-</Text>;
  }

  const shown = variant === 'panel' ? groups : groups.slice(0, MAX_DISPLAY_SOURCES);
  const remaining = variant === 'panel' ? [] : groups.slice(MAX_DISPLAY_SOURCES).flat();

  return (
    <InlineStack alignBlock="center" wrap={variant === 'panel' ? 'wrap' : undefined} className={styles.marks}>
      {shown.map((group, index) => {
        const [source] = group;
        // Keyed by position: a source carries no id of its own, and two
        // writes from the same place are identical in every field the
        // schema has.
        return source == null ? null : (
          <SourceMark key={`${source.type}-${index}`} source={source} count={group.length} />
        );
      })}
      {remaining.length > 0 ? (
        <Tooltip content={remaining.map((source) => getSourceIconInfo(source).title).join(', ')}>
          <Box className={styles.overflowMark}>
            <Text color="placeholder" size="xs">
              +{remaining.length}
            </Text>
          </Box>
        </Tooltip>
      ) : null}
    </InlineStack>
  );
}

/** What one source calls itself: its campaign, its kind, or the generic word. */
function sourceTitle(source: Source, generic: string, campaignSingular: string, newsletterSingular: string): string {
  if (!isEmptyString(source.campaign_name ?? '')) {
    return `${campaignSingular}: ${source.campaign_name}`;
  }
  if (source.type === ACTIVITY_LOG_SOURCE.Newsletter) {
    return newsletterSingular;
  }
  return generic;
}

type SourceMarkProps = {
  source: Source;
  count: number;
};

function SourceMark({ source, count }: SourceMarkProps) {
  const { icon, imageSrc, title } = getSourceIconInfo(source);
  const isMultiSource = count > 1;
  const label = isMultiSource
    ? `Multiple sources (${title} and others)`
    : // A single mark says *which* automation or broadcast wrote the value,
      // not merely that one did: in the panel an attribute can carry eighty
      // marks, and the generic word on all of them tells a reader nothing.
      // The page this replaces names it the same way, and only when the mark
      // stands for one source (`data-source-icon/index.gts:59-68`).
      sourceTitle(source, title, 'Automation', 'One-time send');

  const glyph =
    imageSrc != null ? (
      <Image src={imageSrc} alt={label} width="16" height="16" />
    ) : (
      // Drawn in the row's own ink, as the page this replaces draws it: the
      // circle is what sets the mark apart, not a lighter glyph.
      <Icon name={icon ?? 'import'} size="sm" flexShrink="0" />
    );

  // Written from one place: a single circle, lifted over the empty ones
  // standing behind a multi-source mark.
  if (!isMultiSource) {
    return (
      <Tooltip content={label}>
        <Box className={styles.elevatedMark}>{glyph}</Box>
      </Tooltip>
    );
  }

  // Written from several: two empty circles peek out behind the mark, which
  // is how the page this replaces says "and others" without a second glyph
  // (`data-source-icon/index.gts` renders the same three boxes).
  return (
    <InlineStack alignBlock="center" className={styles.marks}>
      <Tooltip content={label}>
        <Box className={styles.mark}>{glyph}</Box>
      </Tooltip>
      <Box className={styles.stackedMark} />
      <Box className={styles.deepStackedMark} />
    </InlineStack>
  );
}
